// @ts-nocheck
import React, { useState, useEffect, useRef } from "react";
import { Button, Spinner, Modal } from "react-bootstrap";
import { FaFileUpload, FaTimes, FaDownload, FaExchangeAlt } from "react-icons/fa";
import {
  downloadDocumentoByIdentificador,
  deleteDocumentoByIdentificador,
} from "../../../../../../services";
import NotificationToast from "../../../../../../components/NotificationToast/NotificationToast";
import { useForm } from "../../../../../../context/FormContext";

export interface UploadedFileItem {
  nomeArquivo?: string;
  identificador?: string;
  name?: string;
  size?: number;
  type?: string;
  lastModified?: number;
}

const UploadSection = ({
  idResposta,
  nomeArquivo,
  onUpload,
  uploadedFiles = {},
  alternativas = [],
  podeTrocarArquivo = false,
  modoAdequacao = false,
  disabled = false,
}) => {
  const [selectedFiles, setSelectedFiles] = useState<UploadedFileItem[]>([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [fileIndexToDelete, setFileIndexToDelete] = useState<number | null>(null);
  const [isUploading, _setIsUploading] = useState(false);
  const { setIsLoading } = useForm();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [downloadingId, setDownloadingId] = useState<string | null>(null);
  const _nome = nomeArquivo.toLowerCase();

  const aceitaPDF = true;
  const aceitaXLSX = false;

  useEffect(() => {
    const arquivos = uploadedFiles?.[nomeArquivo] || [];
    setSelectedFiles(arquivos);
  }, [uploadedFiles, nomeArquivo]);

  const uploadBloqueado = disabled;

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (uploadBloqueado) return;
    const files = Array.from(event.target.files ?? []);
    if (files.length === 0) return;

    const originalFile = files[0] as File;
    const fileExtension = originalFile.name.split(".").pop()?.toLowerCase() ?? "";

    const formatoValido = fileExtension === "pdf";

    if (!formatoValido) {
      const msg =
        aceitaXLSX && !aceitaPDF
          ? "Apenas arquivos XLSX são permitidos para este campo."
          : "Apenas arquivos PDF são permitidos.";
      setToastMessage(msg);
      setShowToast(true);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const MAX_SIZE_MB = 30;
    const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

    if (originalFile.size > MAX_SIZE_BYTES) {
      setToastMessage(`O arquivo excede o limite de ${MAX_SIZE_MB} MB.`);
      setShowToast(true);
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    const finalName = `${nomeArquivo}.${fileExtension}`;
    const renamedFile = new File([originalFile], finalName, {
      type: originalFile.type,
      lastModified: originalFile.lastModified,
    });
    (renamedFile as File & { nomeArquivo?: string }).nomeArquivo = finalName;

    const novosArquivos = [renamedFile];
    setSelectedFiles(novosArquivos);
    onUpload(nomeArquivo, novosArquivos);
  };

  const confirmRemoveFile = (index) => {
    if (uploadBloqueado) return;
    const file = selectedFiles[index];
    if (!file?.identificador) {
      const updatedFiles = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(updatedFiles);
      onUpload(nomeArquivo, updatedFiles);
      return;
    }
    setFileIndexToDelete(index);
    setShowConfirmModal(true);
  };

  const handleRemoveConfirmed = async () => {
    const index = fileIndexToDelete;
    if (index == null) return;
    const fileToDelete = selectedFiles[index];
    if (!fileToDelete?.identificador) return;
    try {
      setIsLoading(true);
      await deleteDocumentoByIdentificador(fileToDelete.identificador);
      const updatedFiles = selectedFiles.filter((_, i) => i !== index);
      setSelectedFiles(updatedFiles);
      onUpload(nomeArquivo, updatedFiles);
    } catch (error) {
      console.error("Erro ao deletar arquivo:", error);
      setToastMessage("Erro ao deletar o arquivo. Tente novamente.");
      setShowToast(true);
    } finally {
      setShowConfirmModal(false);
      setFileIndexToDelete(null);
      setIsLoading(false);
    }
  };

  const handleDownload = async (identificador, nomeArquivo) => {
    if (downloadingId) return;
    setDownloadingId(identificador);
    try {
      await downloadDocumentoByIdentificador(identificador, nomeArquivo);
    } catch (error) {
      console.error("Erro ao fazer download:", error);
      setToastMessage("Não foi possível abrir/baixar o arquivo.");
      setShowToast(true);
    } finally {
      setDownloadingId(null);
    }
  };

  const getInstrucaoFormatada = () => {
    if (aceitaPDF) {
      return (
        <>
          O arquivo deverá estar no formato <strong>PDF</strong> de no máximo 30
          MB.{" "}
          <span className="clique-aqui">
            Clique no botão abaixo para escolher.
          </span>
        </>
      );
    }

    if (aceitaXLSX) {
      return (
        <>
          O arquivo deverá estar no formato <strong>XLSX</strong> de no máximo
          30 MB.{" "}
          <span className="clique-aqui">
            Clique no botão abaixo para escolher.
          </span>
        </>
      );
    }

    return <>Formato de arquivo não permitido.</>;
  };

  return (
    <div
      className="upload-section mt-2"
      onDragOver={(e) => e.preventDefault()}
      onDrop={(e) => {
        e.preventDefault();
        if (uploadBloqueado) return;
        const files = Array.from(e.dataTransfer.files);
        if (files.length > 0) {
          handleFileChange({ target: { files } } as React.ChangeEvent<HTMLInputElement>);
        }
      }}
    >
      {selectedFiles.length === 0 && !uploadBloqueado && (
        <>
          <p className="text-muted small">{getInstrucaoFormatada()}</p>
          <Button
            variant="outline-primary"
            onClick={() => {
              if (fileInputRef.current) {
                fileInputRef.current.click();
              }
            }}
            className="upload-btn"
            disabled={isUploading || uploadBloqueado}
          >
            {isUploading ? (
              <>
                <Spinner
                  animation="border"
                  size="sm"
                  role="status"
                  className="me-2"
                />
                Carregando...
              </>
            ) : (
              <>
                <FaFileUpload className="me-2" />
                Escolher Arquivo
              </>
            )}
          </Button>
        </>
      )}

      <input
        type="file"
        ref={fileInputRef}
        style={{ display: "none" }}
        multiple={false}
        accept={aceitaPDF ? ".pdf" : ".xlsx"}
        onChange={handleFileChange}
        disabled={uploadBloqueado}
      />

      {selectedFiles.length > 0 && (
        <div className="file-preview-container mt-2">
          {selectedFiles.map((file, index) => (
            <div
              key={index}
              className="file-preview d-flex justify-content-between align-items-center border p-2 rounded mb-2"
            >
              <span className="text-truncate" style={{ maxWidth: "70%" }}>
                {file.nomeArquivo}
              </span>
              <div className="d-flex">
                {file.identificador && (
                  <Button
                    variant="success"
                    size="sm"
                    className="me-2"
                    disabled={downloadingId === file.identificador}
                    onClick={(e) => {
                      e.preventDefault();
                      handleDownload(file.identificador, file.nomeArquivo);
                    }}
                    title={
                      downloadingId === file.identificador
                        ? "Processando..."
                        : "Baixar Arquivo"
                    }
                  >
                    {downloadingId === file.identificador ? (
                      <Spinner as="span" animation="border" size="sm" role="status" />
                    ) : (
                      <FaDownload />
                    )}
                  </Button>
                )}
                {podeTrocarArquivo && !uploadBloqueado && (
                  <Button
                    variant="primary"
                    size="sm"
                    className="me-2"
                    onClick={() => {
                      if (fileInputRef.current) {
                        fileInputRef.current.value = null;
                        fileInputRef.current.click();
                      }
                    }}
                    title="Trocar arquivo"
                    disabled={uploadBloqueado}
                  >
                    <FaExchangeAlt />
                  </Button>
                )}

                {!podeTrocarArquivo && !modoAdequacao && !disabled && (
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => confirmRemoveFile(index)}
                    title="Remover arquivo"
                  >
                    <FaTimes />
                  </Button>
                )}

              </div>
            </div>
          ))}
        </div>
      )}

      <Modal
        show={showConfirmModal}
        onHide={() => setShowConfirmModal(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Confirmar exclusão</Modal.Title>
        </Modal.Header>
        <Modal.Body>Deseja realmente deletar este arquivo?</Modal.Body>
        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() => setShowConfirmModal(false)}
          >
            Cancelar
          </Button>
          <Button
            style={{ backgroundColor: "#005ca9", borderColor: "#005ca9" }}
            onClick={handleRemoveConfirmed}
          >
            Confirmar
          </Button>
        </Modal.Footer>
      </Modal>

      <NotificationToast
        show={showToast}
        onClose={() => setShowToast(false)}
        message={toastMessage}
        type="error"
      />
    </div>
  );
};

export default UploadSection;
